import { useMutation, useQuery } from 'react-query';
import { api } from './service-api';
import { httpClient } from 'redux/reducers/settings';

export interface DailyFeedback {
  status: string;
  statusCode: number;
  data: FeedBackResult[];
  message: string;
  //   title: any;
}

export interface SuccessResponse<T> {
  status: string;
  statusCode: number;
  data: T[];
  message: string;
}

export interface LunchFeedbackResponse {
  routine_date: string;
  routine_session: number;
  feedback_enabled: boolean;
  feedback_submit_status: boolean;
  routine: number;
}

export interface TrainingFeedbackResponse {
  training_data: {
    id: number;
    training_name: string;
  }[];
  feedback_submit_status: boolean;
  routine: number;
  feedback_enabled:boolean
}

interface Data {
  count: number;
  next: any;
  previous: any;
  results: FeedBackResult[];
}

interface IcourseTopic {
  id: number;
  title: string;
}
interface FeedBackResult {
  id: number;
  routine_session: RoutineSession;
  routine_date: string;
  altered_start_time: any;
  altered_end_time: any;
  course_topic: IcourseTopic;
  feedback_enabled: boolean;
  feedback_submit_status: boolean;
}

export interface RoutineSession {
  id: number;
  routine: number;
  session_name: string;
  start_time: string;
  end_time: string;
}

export interface FeedBackQuestion {
  status: string;
  statusCode: number;
  data: FeedBackQuestionResult[];
  message: string;
}

interface FeedbackQuestionData {
  count: number;
  next: any;
  previous: any;
  results: FeedBackQuestionResult[];
}

interface FeedBackQuestionResult {
  routine_feedback_que_id: number;
  feedback_question_details: FeedbackQuestionDetail[];
  training_routine: number;
  training_routine_details: TrainingRoutineDetails;
}

interface FeedbackQuestionDetail {
  tms_feedback_question_id: string;
  question: string;
  question_np: string;
  question_type: string;
}

interface TrainingRoutineDetails {
  routine_master_id: number;
  training_details: TrainingDetails;
}

interface TrainingDetails {
  id: number;
  training_code: string;
  training_name: string;
  training_description: string;
  courses: Course[];
  image: any;
}

interface Course {
  id: number;
  title: string;
}

export interface FeedbackModule {
  status: string;
  statusCode: number;
  data: Daum[];
  message: string;
}

export interface Daum {
  routine_session: number;
  course: CourseDetail;
  feedback_enabled: boolean;
  feedback_submit_status: boolean;
}

export interface CourseDetail {
  course_id: string;
  title: string;
  code: string;
}

interface FeedBackModuleData {
  count: number;
  next: any;
  previous: any;
  results: FeedbackModuleResult[];
}

interface FeedbackModuleResult {
  course_id: string;
  title: string;
  code: string;
}

export interface ISaveFeedbackAnswer {
  daily_session: string;
  routine_feedback_que_id: string;
  feedback_question_id: string;
  feedback_point: string;
}

const fetchDailyFeedback = () => {
  return httpClient.get<DailyFeedback>(api.dailyFeedback);
};

const useDailyFeedback = () => {
  return useQuery(api.dailyFeedback, fetchDailyFeedback, {
    select: (data) => data?.data?.data
  });
};

const fetchFeedbackQuestion = (routineId: string, questionFor: string) => () => {
  return httpClient.get<FeedBackQuestion>(api.feedbackQuestion, {
    params: {
      routineId: routineId,
      questionFor: questionFor
    }
  });
};

const useFeedbackQuestion = (routineId: string, questionFor: string) => {
  return useQuery(
    [api.feedbackQuestion, routineId, questionFor],
    fetchFeedbackQuestion(routineId, questionFor),
    {
      select: (data) => {
        return data?.data?.data;
      },
      enabled: !!routineId
    }
  );
};

const fetchFeedbackModule = () => {
  return httpClient.get<FeedbackModule>(api.fetchFeedbackModule);
};

const useFeedbackModule = () => {
  return useQuery(api.fetchFeedbackModule, fetchFeedbackModule, {
    select: (data) => data?.data?.data
  });
};

const lunchFeedbackModule = () => {
  return httpClient.get<SuccessResponse<LunchFeedbackResponse>>(api.fetchLunchFeedbackModule);
};

const useLunchFeedbackModule = () => {
  return useQuery(api.fetchLunchFeedbackModule, lunchFeedbackModule, {
    select: (data) => data?.data?.data
  });
};

// const lunchFeedbackQuestions =
//   (routineId: number, routineDate: string) => () => {
//     return httpClient.get(
//       api.fetchLunchFeedbackQuestions
//         .replace("{routineId}", routineId.toString())
//         .replace("{feedbackDate}", routineDate)
//     );
//   };

// const useLunchFeedbackQuestions = (routineId: number, routineDate: string) => {
//   return useQuery(
//     [api.fetchLunchFeedbackQuestions, routineId, routineDate],
//     lunchFeedbackQuestions(routineId, routineDate),
//     {
//       select: (data: any) => data?.data?.data,
//       enabled: !!routineId && !!routineDate
//     }
//   );
// };

const fetchTrainingFeedback = () => {
  return httpClient.get<SuccessResponse<TrainingFeedbackResponse>>(api.fetchTrainingFeedback);
};

const useFetchTrainingFeedback = () => {
  return useQuery(api.fetchTrainingFeedback, fetchTrainingFeedback, {
    select: (data) => data?.data?.data
  });
};

const fetchFeedbackModuleQuestion = (routineId: string) => () => {
  return httpClient.get(api.feedbackQuestion, {
    params: {
      routine_id: routineId,
      question_for: 'module'
    }
  });
};

const useFeedbackModuleQuestion = (routineId: string) => {
  return useQuery([api.feedbackQuestion, routineId], fetchFeedbackModuleQuestion(routineId));
};

const fetchFeedbackLunchQuestion = (routineId: string) => () => {
  return httpClient.get(api.feedbackQuestion, {
    params: {
      routine_id: routineId,
      question_for: 'lunch'
    }
  });
};

const useFeedbackLunchQuestion = (routineId: string) => {
  return useQuery([api.feedbackQuestion, routineId], fetchFeedbackLunchQuestion(routineId));
};

const saveFeedbackAnswer = (saveAnswer: ISaveFeedbackAnswer[]) => {
  return httpClient.post(api.saveFeedbackAnswer, saveAnswer);
};

const saveFeedbackModuleAnswer = (saveAnswer: ISaveFeedbackAnswer[]) => {
  return httpClient.post(api.saveFeedbackModuleAnswer, saveAnswer);
};

const saveTrainingFeedbackAnswer = (saveAnswer: any) => {
  return httpClient.post(api.saveTrainingFeedbackAnswer, saveAnswer);
};

const useMutationSaveTrainingFeedbackAnswer = () => {
  return useMutation(saveTrainingFeedbackAnswer);
};

const saveLunchFeedbackAnswer = (saveAnswer: any) => {
  return httpClient.post(api.saveLunchFeedbackAnswer, saveAnswer);
};

const useMutationSaveLunchFeedbackAnswer = () => {
  return useMutation(saveLunchFeedbackAnswer);
};

const useMutationSaveFeedbackAnswer = () => {
  return useMutation(saveFeedbackAnswer);
};
const useMutationSaveFeedbackModuleAnswer = () => {
  return useMutation(saveFeedbackModuleAnswer);
};

export {
  useDailyFeedback,
  useFeedbackLunchQuestion,
  useFeedbackModule,
  useFeedbackModuleQuestion,
  useFeedbackQuestion,
  useFetchTrainingFeedback,
  useLunchFeedbackModule,
  useMutationSaveFeedbackAnswer,
  useMutationSaveFeedbackModuleAnswer,
  useMutationSaveLunchFeedbackAnswer,
  useMutationSaveTrainingFeedbackAnswer
};
